import React, { useState, useMemo } from 'react';
import { rewardData, validators } from '../../2_data/rewardData.js'; 
import { auth } from "../../firebase.js";
import ReportHeader from './section/ReportHeader.js';
import DailySection from './section/DailySection.js';
import WeeklySection from './section/WeeklySection.js';
import MonthlySection from './section/MonthlySection.js';
import AiModal from './section/AiModal.js';
import { useAppContext } from '../../0_context/AppContext.js';

export default function ReportPage({ isActive }) {
  const {
    calendarCurrentDate,
    moveCalendarMonth,
    captureReportToImage,
    renderCalendarDays,
    workouts = [],
    cardioWorkouts = [],
    supplementsList = [],
    geminiAiPromptRun,
    parseAIReport,
    getRecentWorkoutData
  } = useAppContext();

  const [activeTab, setActiveTab] = useState('daily');
  const [expandedMonthly, setExpandedMonthly] = useState({});
  const [dailySelectedDate, setDailySelectedDate] = useState(new Date());

  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiEvaluationResult, setAiEvaluationResult] = useState('');

  // 평탄화된 데이터(Flat)와 중첩 데이터(Nested)를 모두 호환하는 정규화 함수
  const normalizeData = (list) => {
    if (!Array.isArray(list)) return [];
    const map = new Map();
    const result = [];

    list.forEach((item, index) => {
      if (!item || !item.name) return;
      const date = item.date ? item.date.trim() : '';
      const key = `${date}__${item.name}`;
      
      // item.sets가 있으면 그대로 사용, 없으면(평탄화 데이터) item 자체를 단일 세트로 가공
      const sets = Array.isArray(item.sets) ? item.sets : [{ ...item }];

      if (!map.has(key)) {
        const normalized = {
          id: item.id || Date.now() + index,
          date,
          name: item.name,
          sets: [...sets]
        };
        map.set(key, normalized);
        result.push(normalized);
      } else {
        map.get(key).sets.push(...sets);
      }
    });
    return result;
  };

  const currentWorkouts = useMemo(() => normalizeData(workouts), [workouts]);
  const currentCardioWorkouts = useMemo(() => normalizeData(cardioWorkouts), [cardioWorkouts]);
  const safeSupplementsList = useMemo(() => Array.isArray(supplementsList) ? supplementsList : [], [supplementsList]);

  const achievedRewards = useMemo(() => {
    const achieved = [];
    Object.keys(rewardData).forEach(tab => {
      const rewards = rewardData[tab] || [];
      const validator = validators[tab];
      if (validator) {
        rewards.forEach(reward => {
          if (validator(currentWorkouts, reward)) achieved.push(reward);
        });
      }
    });
    return achieved;
  }, [currentWorkouts]);

  if (!isActive) return null;

  const getAggregateSummary = (dataArray) => {
    let setsCount = 0, totalReps = 0, maxWeight = 0;
    dataArray.forEach(item => {
      if (!item.sets || !Array.isArray(item.sets)) return;
      setsCount += item.sets.length;
      item.sets.forEach(s => {
        const w = parseFloat(s.weight) || 0;
        const r = parseInt(s.reps) || 0;
        totalReps += r;
        if (w > maxWeight) maxWeight = w;
      });
    });
    return { setsCount, totalReps, maxWeight };
  };

  const formatDateStr = (dateObj) => {
    return `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
  };

  const now = new Date();
  const selectedDateStr = formatDateStr(dailySelectedDate);
  const prevSelectedDate = new Date(dailySelectedDate);
  prevSelectedDate.setDate(dailySelectedDate.getDate() - 1);
  const prevDateStr = formatDateStr(prevSelectedDate);

  const displayDateStr = dailySelectedDate.toLocaleDateString('ko-KR', {
    year: 'numeric', month: 'long', day: 'numeric', weekday: 'short'
  });

  const moveDailyDate = (days) => {
    const newDate = new Date(dailySelectedDate);
    newDate.setDate(dailySelectedDate.getDate() + days);
    setDailySelectedDate(newDate);
  };

  const moveTodayDailyDate = () => setDailySelectedDate(new Date());

  const dayOfWeek = now.getDay(); 
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const startOfThisWeek = new Date(now);
  startOfThisWeek.setDate(now.getDate() + diffToMonday);
  const endOfThisWeek = new Date(startOfThisWeek);
  endOfThisWeek.setDate(startOfThisWeek.getDate() + 6);

  const startOfLastWeek = new Date(startOfThisWeek);
  startOfLastWeek.setDate(startOfThisWeek.getDate() - 7);
  const endOfLastWeek = new Date(startOfLastWeek);
  endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);

  const thisWeekStartStr = formatDateStr(startOfThisWeek);
  const thisWeekEndStr = formatDateStr(endOfThisWeek);
  const lastWeekStartStr = formatDateStr(startOfLastWeek);
  const lastWeekEndStr = formatDateStr(endOfLastWeek);

  const selectedDaySummary = getAggregateSummary(currentWorkouts.filter(w => w.date === selectedDateStr));
  const prevDaySummary = getAggregateSummary(currentWorkouts.filter(w => w.date === prevDateStr));
  const thisWeekSummary = getAggregateSummary(currentWorkouts.filter(w => w.date >= thisWeekStartStr && w.date <= thisWeekEndStr));
  const lastWeekSummary = getAggregateSummary(currentWorkouts.filter(w => w.date >= lastWeekStartStr && w.date <= lastWeekEndStr));

  const renderDiff = (currentVal, prevVal, unit) => {
    const diff = currentVal - prevVal;
    if (diff > 0) return <span className="text-rose-500 font-bold text-xs flex items-center ml-1"><span className="material-icons-round text-[14px]">arrow_drop_up</span>{diff}{unit}</span>;
    if (diff < 0) return <span className="text-blue-500 font-bold text-xs flex items-center ml-1"><span className="material-icons-round text-[14px]">arrow_drop_down</span>{Math.abs(diff)}{unit}</span>;
    return <span className="text-slate-300 font-bold text-xs ml-1 flex items-center">-</span>;
  };

  const formatCardioSet = (set) => {
    let details = [];
    if (set.phase) details.push(`[${set.phase}]`);
    if (set.time) details.push(`${set.time}분`);
    if (set.distance) details.push(`${set.distance}km`);
    if (set.reps) details.push(`${set.reps}회`);
    if (set.speed) details.push(`속도 ${set.speed}`);
    if (set.incline !== undefined && set.incline !== '') details.push(`경사도 ${set.incline}`);
    if (set.pace) details.push(`페이스 ${set.pace}`);
    return details.join(' / ');
  };

  const getSelectedDayWorkouts = () => {
    const dayData = currentWorkouts.filter(item => item.date && item.date.trim() === selectedDateStr);
    const summary = {};
    dayData.forEach(item => {
      const name = item.name;
      if (!name || !item.sets || !Array.isArray(item.sets)) return;
      if (!summary[name]) summary[name] = { name, setsCount: 0, maxWeight: 0, totalReps: 0 };
      item.sets.forEach(set => {
        const weight = parseFloat(set.weight) || 0;
        const reps = parseInt(set.reps) || 0;
        summary[name].setsCount += 1;
        if (weight > summary[name].maxWeight) summary[name].maxWeight = weight;
        summary[name].totalReps += reps;
      });
    });
    return Object.values(summary);
  };

  const getSelectedDayCardioWorkouts = () => {
    const dayData = currentCardioWorkouts.filter(item => item.date && item.date.trim() === selectedDateStr);
    const summary = {};
    dayData.forEach(item => {
      const name = item.name;
      if (!name || !item.sets || !Array.isArray(item.sets)) return;
      if (!summary[name]) summary[name] = { name, setsCount: 0, totalTime: 0, totalDistance: 0 };
      item.sets.forEach(set => {
        if (set.time) summary[name].totalTime += parseFloat(set.time);
        if (set.distance) summary[name].totalDistance += parseFloat(set.distance);
        summary[name].setsCount += 1;
      });
    });
    return Object.values(summary);
  };

  const handleCopyDailyWorkout = () => {
    const dayData = currentWorkouts.filter(item => item.date && item.date.trim() === selectedDateStr);
    const cardioDayData = currentCardioWorkouts.filter(item => item.date && item.date.trim() === selectedDateStr);
    if (dayData.length === 0 && cardioDayData.length === 0) {
      alert('복사할 운동 기록이 없습니다.');
      return;
    }
    let copyText = `${displayDateStr} 운동 기록\n\n`;
    if (dayData.length > 0) {
      copyText += `[무산소 운동]\n`;
      dayData.forEach(workout => {
        copyText += `${workout.name}\n`;
        workout.sets.forEach((set, index) => { copyText += `  - ${index + 1}세트: ${set.weight || 0}kg × ${set.reps || 0}회\n`; });
        copyText += '\n';
      });
    }
    if (cardioDayData.length > 0) {
      copyText += `[유산소 운동]\n`;
      cardioDayData.forEach(workout => {
        copyText += `${workout.name}\n`;
        workout.sets.forEach((set, index) => { copyText += `  - ${index + 1}세트: ${formatCardioSet(set)}\n`; });
        copyText += '\n';
      });
    }
    navigator.clipboard.writeText(copyText.trim()).then(() => alert('운동 기록이 클립보드에 복사되었습니다!'));
  };

  const handleCopyWeeklyWorkout = () => {
    const weeklyData = currentWorkouts.filter(item => item.date && item.date >= thisWeekStartStr && item.date <= thisWeekEndStr);
    const weeklyCardioData = currentCardioWorkouts.filter(item => item.date && item.date >= thisWeekStartStr && item.date <= thisWeekEndStr);
    if (weeklyData.length === 0 && weeklyCardioData.length === 0) {
      alert('복사할 이번 주 운동 기록이 없습니다.');
      return;
    }
    const periodStr = `${thisWeekStartStr.slice(5).replace('-', '.')} ~ ${thisWeekEndStr.slice(5).replace('-', '.')}`;
    let copyText = `이번 주 운동 기록 (${periodStr})\n\n`;
    if (weeklyData.length > 0) {
      copyText += `[무산소 운동]\n`;
      weeklyData.forEach(workout => {
        copyText += `[${workout.date}] ${workout.name}\n`;
        workout.sets.forEach((set, index) => { copyText += `  - ${index + 1}세트: ${set.weight || 0}kg × ${set.reps || 0}회\n`; });
        copyText += '\n';
      });
    }
    if (weeklyCardioData.length > 0) {
      copyText += `[유산소 운동]\n`;
      weeklyCardioData.forEach(workout => {
        copyText += `[${workout.date}] ${workout.name}\n`;
        workout.sets.forEach((set, index) => { copyText += `  - ${index + 1}세트: ${formatCardioSet(set)}\n`; });
        copyText += '\n';
      });
    }
    navigator.clipboard.writeText(copyText.trim()).then(() => alert('주간 운동 기록이 클립보드에 복사되었습니다!'));
  };

  const getWeeklyWorkouts = () => {
    const weeklyData = currentWorkouts.filter(item => item.date && item.date >= thisWeekStartStr && item.date <= thisWeekEndStr);
    const summary = {};
    weeklyData.forEach(item => {
      const name = item.name;
      if (!name || !item.sets || !Array.isArray(item.sets)) return;
      if (!summary[name]) summary[name] = { name, absoluteMaxWeight: 0, absoluteMaxReps: 0, history: [] };
      let sessionMaxWeight = 0, sessionMaxReps = 0;
      item.sets.forEach(set => {
        const weight = parseFloat(set.weight) || 0;
        const reps = parseInt(set.reps) || 0;
        if (weight > sessionMaxWeight) { sessionMaxWeight = weight; sessionMaxReps = reps; }
        if (weight > summary[name].absoluteMaxWeight) { summary[name].absoluteMaxWeight = weight; summary[name].absoluteMaxReps = reps; }
      });
      summary[name].history.push({ date: item.date, setsCount: item.sets.length, maxWeight: sessionMaxWeight, maxReps: sessionMaxReps });
    });
    Object.values(summary).forEach(group => group.history.sort((a, b) => new Date(b.date) - new Date(a.date)));
    return { data: Object.values(summary), periodText: `${thisWeekStartStr.slice(5).replace('-', '.')} ~ ${thisWeekEndStr.slice(5).replace('-', '.')}` };
  };

  const getWeeklyCardioWorkouts = () => {
    const weeklyData = currentCardioWorkouts.filter(item => item.date && item.date >= thisWeekStartStr && item.date <= thisWeekEndStr);
    const summary = {};
    weeklyData.forEach(item => {
      const name = item.name;
      if (!name || !item.sets || !Array.isArray(item.sets)) return;
      if (!summary[name]) summary[name] = { name, maxTotalTime: 0, maxTotalDistance: 0, history: [] };
      let sessionTotalTime = 0, sessionTotalDistance = 0;
      item.sets.forEach(set => {
        if (set.time) sessionTotalTime += parseFloat(set.time);
        if (set.distance) sessionTotalDistance += parseFloat(set.distance);
      });
      if (sessionTotalTime > summary[name].maxTotalTime) summary[name].maxTotalTime = sessionTotalTime;
      if (sessionTotalDistance > summary[name].maxTotalDistance) summary[name].maxTotalDistance = sessionTotalDistance;
      summary[name].history.push({ date: item.date, setsCount: item.sets.length, totalTime: sessionTotalTime, totalDistance: sessionTotalDistance });
    });
    Object.values(summary).forEach(group => group.history.sort((a, b) => new Date(b.date) - new Date(a.date)));
    return Object.values(summary);
  };

  const getMonthlyWorkouts = () => {
    const currentYear = calendarCurrentDate.getFullYear();
    const currentMonth = String(calendarCurrentDate.getMonth() + 1).padStart(2, '0');
    const prefix = `${currentYear}-${currentMonth}`;
    const monthlyData = currentWorkouts.filter(item => item.date && item.date.startsWith(prefix));
    const summary = {};
    monthlyData.forEach(item => {
      const name = item.name;
      if (!name || !item.sets || !Array.isArray(item.sets)) return;
      if (!summary[name]) summary[name] = { name, absoluteMaxWeight: 0, absoluteMaxReps: 0, history: [] };
      let sessionMaxWeight = 0, sessionMaxReps = 0;
      item.sets.forEach(set => {
        const weight = parseFloat(set.weight) || 0;
        const reps = parseInt(set.reps) || 0;
        if (weight > sessionMaxWeight) { sessionMaxWeight = weight; sessionMaxReps = reps; }
        if (weight > summary[name].absoluteMaxWeight) { summary[name].absoluteMaxWeight = weight; summary[name].absoluteMaxReps = reps; }
      });
      summary[name].history.push({ date: item.date, setsCount: item.sets.length, maxWeight: sessionMaxWeight, maxReps: sessionMaxReps });
    });
    Object.values(summary).forEach(group => group.history.sort((a, b) => new Date(b.date) - new Date(a.date)));
    return Object.values(summary);
  };

  const getMonthlyCardioWorkouts = () => {
    const currentYear = calendarCurrentDate.getFullYear();
    const currentMonth = String(calendarCurrentDate.getMonth() + 1).padStart(2, '0');
    const prefix = `${currentYear}-${currentMonth}`;
    const monthlyData = currentCardioWorkouts.filter(item => item.date && item.date.startsWith(prefix));
    const summary = {};
    monthlyData.forEach(item => {
      const name = item.name;
      if (!name || !item.sets || !Array.isArray(item.sets)) return;
      if (!summary[name]) summary[name] = { name, maxTotalTime: 0, maxTotalDistance: 0, history: [] };
      let sessionTotalTime = 0, sessionTotalDistance = 0;
      item.sets.forEach(set => {
        if (set.time) sessionTotalTime += parseFloat(set.time);
        if (set.distance) sessionTotalDistance += parseFloat(set.distance);
      });
      if (sessionTotalTime > summary[name].maxTotalTime) summary[name].maxTotalTime = sessionTotalTime;
      if (sessionTotalDistance > summary[name].maxTotalDistance) summary[name].maxTotalDistance = sessionTotalDistance;
      summary[name].history.push({ date: item.date, setsCount: item.sets.length, totalTime: sessionTotalTime, totalDistance: sessionTotalDistance });
    });
    Object.values(summary).forEach(group => group.history.sort((a, b) => new Date(b.date) - new Date(a.date)));
    return Object.values(summary);
  };

  const toggleMonthlyExpand = (name) => setExpandedMonthly(prev => ({ ...prev, [name]: !prev[name] }));

  const selectedDayWorkoutsDetail = getSelectedDayWorkouts();
  const selectedDayCardioDetail = getSelectedDayCardioWorkouts();
  const weeklyDataObj = getWeeklyWorkouts();
  const weeklyWorkoutsDetail = weeklyDataObj.data;
  const weeklyCardioDetail = getWeeklyCardioWorkouts();
  const weeklyPeriodText = weeklyDataObj.periodText;
  const monthlyWorkouts = getMonthlyWorkouts();
  const monthlyCardioWorkouts = getMonthlyCardioWorkouts();

  const handleEvaluateDailyWorkout = async () => {
    const authStatus = localStorage.getItem('logup_auth_status');
    const userUid = auth.currentUser?.uid;
    if (authStatus !== 'google' || userUid == null) {
      alert('로그인 이후 사용 가능합니다.');
      return;
    }

    const dayData = currentWorkouts.filter(item => item.date && item.date.trim() === selectedDateStr);
    const cardioDayData = currentCardioWorkouts.filter(item => item.date && item.date.trim() === selectedDateStr);

    if (dayData.length === 0 && cardioDayData.length === 0) {
      alert('평가할 운동 기록이 없습니다.');
      return;
    }

    let workoutText = `${displayDateStr} 운동 기록\n\n`;
    if (dayData.length > 0) {
      workoutText += `[무산소]\n`;
      dayData.forEach(workout => {
        workoutText += `${workout.name}\n`;
        workout.sets.forEach((set, index) => { workoutText += `  - ${index + 1}세트: ${set.weight || 0}kg × ${set.reps || 0}회\n`; });
        workoutText += '\n';
      });
    }
    if (cardioDayData.length > 0) {
      workoutText += `[유산소]\n`;
      cardioDayData.forEach(workout => {
        workoutText += `${workout.name}\n`;
        workout.sets.forEach((set, index) => {
          workoutText += `  - ${index + 1}세트: ${formatCardioSet(set)}\n`;
        });
        workoutText += '\n';
      });
    }

    setIsAiModalOpen(true);
    setIsAiLoading(true);
    setAiEvaluationResult('');

    try {
      let inputValue = ``;
      const userSavedProfileItem = localStorage.getItem('logup_user_profile');
      const userSavedProfileItemParse = userSavedProfileItem ? JSON.parse(userSavedProfileItem) : { age: '', height: '', weight: '', muscleMass: '', bodyFat: '', lastUpdated: '' };
      inputValue += `- 신체 정보 : 
      나이 ${userSavedProfileItemParse.age}\n 
      키 ${userSavedProfileItemParse.height}\n
      몸무게 ${userSavedProfileItemParse.weight}\n
      골격근량 ${userSavedProfileItemParse.muscleMass}\n
      체지방량 ${userSavedProfileItemParse.bodyFat}\n
      (저장 일자 ${userSavedProfileItemParse.lastUpdated})\n`;

      if (safeSupplementsList != null && safeSupplementsList.length !== 0) {
        let parse = `- 영양제 복용정보 : `;
        safeSupplementsList.forEach((v) => { if (v.date === selectedDateStr) parse += v.text; });
        inputValue += `${parse} \n`;
      }
      
      inputValue += `- 운동 정보 : \n${workoutText.trim()}`;
      let recent = await getRecentWorkoutData();

      if (recent != null && recent.length > 0) {
        recent = JSON.stringify(recent);
        recent += `이전 운동 기록\n\n`;
      }

      const resultText = await geminiAiPromptRun({ input: inputValue, recent, date: dailySelectedDate });
      setAiEvaluationResult(resultText);
    } catch (error) {
      console.error(error);
      setAiEvaluationResult('AI 평가를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setIsAiLoading(false);
    }
  };

  return (
    <section id="page-report" className="page active mb-24">
      <ReportHeader activeTab={activeTab} setActiveTab={setActiveTab} />

      {activeTab === 'daily' && (
        <DailySection
          displayDateStr={displayDateStr}
          moveDailyDate={moveDailyDate}
          moveTodayDailyDate={moveTodayDailyDate}
          selectedDayWorkoutsDetail={selectedDayWorkoutsDetail}
          selectedDayCardioDetail={selectedDayCardioDetail}
          selectedDaySummary={selectedDaySummary}
          prevDaySummary={prevDaySummary}
          renderDiff={renderDiff}
          handleCopyDailyWorkout={handleCopyDailyWorkout}
          handleEvaluateDailyWorkout={handleEvaluateDailyWorkout}
        />
      )}

      {activeTab === 'weekly' && (
        <WeeklySection
          weeklyPeriodText={weeklyPeriodText}
          weeklyWorkoutsDetail={weeklyWorkoutsDetail}
          weeklyCardioDetail={weeklyCardioDetail}
          thisWeekSummary={thisWeekSummary}
          lastWeekSummary={lastWeekSummary}
          renderDiff={renderDiff}
          captureReportToImage={captureReportToImage}
          handleCopyWeeklyWorkout={handleCopyWeeklyWorkout}
        />
      )}

      {activeTab === 'monthly' && (
        <MonthlySection
          calendarCurrentDate={calendarCurrentDate}
          moveCalendarMonth={moveCalendarMonth}
          renderCalendarDays={renderCalendarDays}
          achievedRewards={achievedRewards}
          monthlyWorkouts={monthlyWorkouts}
          monthlyCardioWorkouts={monthlyCardioWorkouts}
          expandedMonthly={expandedMonthly}
          toggleMonthlyExpand={toggleMonthlyExpand}
          captureReportToImage={captureReportToImage}
        />
      )}

      <AiModal
        isAiModalOpen={isAiModalOpen}
        setIsAiModalOpen={setIsAiModalOpen}
        isAiLoading={isAiLoading}
        aiEvaluationResult={parseAIReport(aiEvaluationResult)}
      />
    </section>
  );
}